import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import {
  BehaviorSubject,
  finalize,
  map,
  Observable,
  combineLatest,
  tap,
  switchMap,
  from,
  shareReplay,
  filter,
} from 'rxjs';
import {
  BillableItem,
  BillingReport,
  CustomerPlan,
  SubscriptionStatus,
  V2SpaceDurationBillableGroup,
} from 'src/app/models/payment';
import {
  BillableFeature,
  BillingFrequency,
  BillingType,
  BillingVersion,
} from '@pncl/common-models';
import { PaymentService } from 'src/app/services/payment.service';
import { ReportsService } from 'src/app/services/reports.service';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { Institution } from 'src/app/models/user';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { TranslateService } from '@ngx-translate/core';
import { CustomerSupportService } from 'src/app/services/customer-support.service';
import { UserService } from 'src/app/services/user.service';
import { TierService } from 'src/app/services/tier.service';
import { validatePaywallConfigurationForInstitution } from '../billing-usage-utils';
interface BillableCategoryDetail {
  header: string;
  unitDescText: string;
}

interface StepBasedCategoryDetail {
  getDescription: () => string;
  getInfoTooltip?: (item: BillableItem) => string;
}

/** A single itemized row in the step-based billing summary table. */
interface StepBasedBillingLineItem {
  description: string;
  infoTooltip?: string;
  quantity: number;
  unitPrice?: number;
  amount: number;
}

/** Monthly plan-credit deduction applied against step-based usage, shown as its own row. */
interface StepBasedMonthlyCreditDeduction {
  amount: number;
  tooltip: string;
}

/** Precomputed data consumed by the step-based billing summary template. */
interface StepBasedBillingSummary {
  balanceDue: number;
  lineItems: StepBasedBillingLineItem[];
  subtotal: number;
  monthlyCreditDeduction?: StepBasedMonthlyCreditDeduction;
}

interface BillingData {
  billingReport: BillingReport;
  customerPlan: CustomerPlan;
  stepBasedBillingSummary?: StepBasedBillingSummary;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

@Component({
  selector: 'app-plan-and-usage',
  templateUrl: './plan-and-usage.component.html',
  styleUrls: ['./plan-and-usage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanAndUsageComponent {
  @Output() viewPlans = new EventEmitter<void>();
  protected isTrialInstitution$: Observable<boolean>;
  protected institutionIsOnFreeTier$: Observable<boolean>;
  protected isAutomaticBillingWithoutSubscription$: Observable<boolean>;
  protected isLoading$ = new BehaviorSubject(false);
  protected readonly IconTypes = IconTypes;
  protected skeletonBars = Array.from({ length: 6 });
  protected BillingType = BillingType;
  protected SubscriptionStatus = SubscriptionStatus;
  protected institution: Institution;
  protected hidePaymentPlans = false;
  protected isSeatBased = false;
  protected billingData$: Observable<BillingData>;
  protected planId?: string;

  billingFrequencyLabels: Record<string, string> = {
    [BillingFrequency.Monthly]: this.translateService.instant('Monthly'),
    [BillingFrequency.Annually]: this.translateService.instant('Annually'),
  };
  private readonly height = 550;
  private readonly width = 500;

  billableCategoriesDetails: Record<any, BillableCategoryDetail> = {
    [BillableFeature.SpaceDuration]: {
      header: 'Space Hours',
      unitDescText: ' user hours billed',
    },
    [BillableFeature.WebViewerDuration]: {
      header: 'Web Viewer Hours',
      unitDescText: ' hours used',
    },
    [BillableFeature.CloudRecordingDuration]: {
      header: 'Cloud Recording',
      unitDescText: ' hours recorded',
    },
  };

  private readonly stepBasedCategoryDetails: Partial<
    Record<BillableFeature, StepBasedCategoryDetail>
  > = {
    [BillableFeature.WebViewerDuration]: {
      getDescription: () => this.translateService.instant('Webviewer usage'),
      getInfoTooltip: (item) =>
        this.translateService.instant(
          "You've used {{totalUsage}}/{{totalAvailableCredits}} hours of webviewer usage.\nOnce you pass this limit, overages will be added.",
          {
            totalUsage: item.totalUsage,
            totalAvailableCredits: item.totalAvailableCredits || 0,
          },
        ),
    },
    [BillableFeature.CloudRecordingDuration]: {
      getDescription: () => this.translateService.instant('Cloud recording usage'),
      getInfoTooltip: (item) =>
        this.translateService.instant(
          "You've used {{totalUsage}}/{{totalAvailableCredits}} hours of cloud recording usage.\nOnce you pass this limit, overages will be added.",
          {
            totalUsage: item.totalUsage,
            totalAvailableCredits: item.totalAvailableCredits || 0,
          },
        ),
    },
  };

  readonly BILLABLE_FEATURE = BillableFeature;

  constructor(
    private readonly reportsService: ReportsService,
    private readonly telemetryService: TelemetryService,
    private readonly customerSupportService: CustomerSupportService,
    private readonly userService: UserService,
    private readonly paymentService: PaymentService,
    private readonly translateService: TranslateService,
    private readonly tierService: TierService,
  ) {
    this.institution = this.userService.user.value!.user.institution;
    this.hidePaymentPlans = !!this.institution.paywallConfig?.hidePaymentPlans;
    this.isSeatBased = this.paymentService.isSeatBasedInstitution(this.institution);
    this.billingData$ = this.fetchBillingPlanAndUsageReport();

    // Reactive observables for better template organization
    this.institutionIsOnFreeTier$ = this.tierService.isInstitutionOnFreeTier(
      this.institution.tierConfiguration?.tier,
    );

    this.isAutomaticBillingWithoutSubscription$ = this.userService.user.pipe(
      map(
        (res) =>
          res?.user?.institution?.paywallConfig?.billingType === BillingType.Automatic &&
          !res?.user?.institution?.billingStatus?.subscription,
      ),
    );

    this.isTrialInstitution$ = this.userService.user.pipe(
      filter((res) => !!res?.user),
      switchMap((res) =>
        from(this.paymentService.checkIsTrialInstitution(res!.user!.institution!)),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const errorMsg = validatePaywallConfigurationForInstitution(this.institution);
    if (errorMsg) {
      this.logPaywallConfigurationErrorsToFullStory(errorMsg);
    }
  }

  private fetchBillingPlanAndUsageReport(): Observable<BillingData> {
    this.isLoading$.next(true);
    const billingReport$ = this.reportsService.getBillingUsageReport();

    const customerPlan$ = this.paymentService.getPlanDetailsByBillingType(
      this.institution.paywallConfig?.billingType ?? BillingType.Manual,
    );

    return combineLatest([billingReport$, customerPlan$]).pipe(
      map(([billingReport, customerPlan]) => ({
        billingReport,
        customerPlan,
        stepBasedBillingSummary: this.buildStepBasedBillingSummary(billingReport),
      })),
      tap(({ customerPlan }) => {
        this.planId = customerPlan.planId;
      }),
      finalize(() => this.isLoading$.next(false)),
    );
  }

  /**
   * Builds the itemized view model for the step-based billing summary.
   * Returns undefined when the report has no step-based breakdown, in which case the
   * legacy usage-progress UI is rendered instead.
   */
  private buildStepBasedBillingSummary(
    billingReport: BillingReport,
  ): StepBasedBillingSummary | undefined {
    if (
      billingReport.billingVersion !== BillingVersion.V2 ||
      !billingReport.spaceDurationV2Breakdown
    ) {
      return undefined;
    }
    const breakdown = billingReport.spaceDurationV2Breakdown;

    // Sessions and additional-participant rows are always shown for step-based plans,
    // even at zero usage, since they represent the two core components of the
    // step-based SpaceDuration model.
    const groupLineItems = breakdown.billableGroups.flatMap((group) =>
      this.buildStepBasedGroupLineItems(group),
    );
    const featureLineItems = billingReport.billableItems.flatMap((item) => {
      const lineItem = this.buildStepBasedFeatureLineItem(item);
      return lineItem ? [lineItem] : [];
    });
    const lineItems = [...groupLineItems, ...featureLineItems];

    const subtotal = round2(lineItems.reduce((sum, item) => sum + item.amount, 0));

    const monthlyCreditDeduction: StepBasedMonthlyCreditDeduction | undefined =
      breakdown.allowanceDollars > 0
        ? {
            amount: round2(Math.min(breakdown.totalUsageDollars, breakdown.allowanceDollars)),
            tooltip: this.translateService.instant(
              'The minimum monthly credit included in your current plan',
            ),
          }
        : undefined;

    return {
      balanceDue: billingReport.totalCharges,
      lineItems,
      subtotal,
      monthlyCreditDeduction,
    };
  }

  private buildStepBasedGroupLineItems(
    group: V2SpaceDurationBillableGroup,
  ): StepBasedBillingLineItem[] {
    return [
      {
        description: this.translateService.instant('Sessions (up to {{count}} users)', {
          count: group.groupSessionThreshold,
        }),
        quantity: group.totalGroupSessionHours,
        unitPrice: group.groupSessionRate,
        amount: group.totalGroupSessionCredits,
      },
      {
        description: this.translateService.instant('Additional participants'),
        quantity: group.totalAdditionalParticipantHours,
        unitPrice: group.additionalParticipantRate,
        amount: group.totalAdditionalParticipantCredits,
      },
    ];
  }

  private buildStepBasedFeatureLineItem(item: BillableItem): StepBasedBillingLineItem | undefined {
    const featureConfig = this.stepBasedCategoryDetails[item.feature];
    if (item.feature === BillableFeature.SpaceDuration || !featureConfig) {
      return undefined;
    }

    // Quantity shows overage hours so `quantity * unitPrice` matches `amount`.
    // Gross usage vs. included credits stays visible via the row's info tooltip.
    const overageHours = Math.max(0, item.totalUsage - (item.totalAvailableCredits ?? 0));

    return {
      description: featureConfig.getDescription(),
      infoTooltip: featureConfig.getInfoTooltip?.(item),
      quantity: overageHours,
      unitPrice: item.billableRate || undefined,
      amount: item.billableCharges,
    };
  }

  private logPaywallConfigurationErrorsToFullStory(message: string) {
    this.telemetryService.log('warn', message);
  }

  async createPortalSession(): Promise<void> {
    this.paymentService.openBufferWindow('Management Portal', this.width, this.height);
    this.paymentService.handlePortalSession(String(this.planId));
  }

  openIntercom(): void {
    this.customerSupportService.openSupport();
  }

  onViewPlans(): void {
    this.viewPlans.emit();
  }
}
